/*
 * newsbot - forked from autobrr
 */

import { Fragment, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import {
  EllipsisHorizontalIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowPathIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline";

import { useToggle } from "@hooks/hooks";
import { classNames, simplifyDate } from "@utils";
import { toast } from "@components/hot-toast";
import Toast from "@components/notifications/Toast";
import { DeleteModal } from "@components/modals";
import { EmptySimple } from "@components/emptystates";
import { Checkbox } from "@components/Checkbox";
import { Section } from "./_components";
import { useFeeds, useFeedGroups, feedKeys } from "@api/queries";
import { api } from "@api/client";
import { Feed, FeedGroup } from "@/types";
import { FeedHealthTable } from "@screens/dashboard/FeedHealthTable";

function FeedSettings() {
  const { data: feeds, isLoading } = useFeeds();
  const { data: feedGroups, isLoading: feedGroupsLoading } = useFeedGroups();

  if (isLoading || feedGroupsLoading) {
    return (
      <Section title="Feeds" description="Manage RSS feeds.">
        <div className="animate-pulse">Loading...</div>
      </Section>
    );
  }

  return (
    <Section title="Feeds" description="Manage RSS feeds.">
      {feeds && feeds.length > 0 ? (
        <ul className="min-w-full relative">
          <li className="grid grid-cols-12 border-b border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wider">
            <div className="flex col-span-2 sm:col-span-1 pl-4 py-3">Enabled</div>
            <div className="col-span-5 sm:col-span-3 pl-10 sm:pl-12 py-3">Name</div>
            <div className="hidden sm:flex col-span-2 py-3">Group</div>
            <div className="hidden md:flex col-span-2 py-3">URL</div>
            <div className="hidden md:flex col-span-1 py-3">Tier</div>
            <div className="hidden md:flex col-span-2 px-4 py-3">Last Run</div>
          </li>
          {feeds.map((feed) => (
            <ListItem key={feed.id} feed={feed} feedGroups={feedGroups || []} />
          ))}
        </ul>
      ) : (
        <EmptySimple title="No feeds" subtitle="Add a feed to get started" />
      )}
      <div className="border-t border-gray-200 dark:border-gray-700 mt-8 pt-2">
        <FeedHealthTable variant="settings" />
      </div>
    </Section>
  );
}

interface ListItemProps {
  feed: Feed;
  feedGroups: FeedGroup[];
}

function ListItem({ feed, feedGroups }: ListItemProps) {
  const [enabled, setEnabled] = useState(feed.enabled);
  const queryClient = useQueryClient();

  const feedGroup = feedGroups.find(g => g.id === feed.feed_group_id);

  const updateMutation = useMutation({
    mutationFn: (status: boolean) =>
      api.patch(`/feeds/${feed.id}/enabled`, { enabled: status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feedKeys.all });
      toast.custom((t) => (
        <Toast
          type="success"
          body={`${feed.name} was ${enabled ? "disabled" : "enabled"} successfully.`}
          t={t}
        />
      ));
    },
  });

  const toggleActive = (status: boolean) => {
    setEnabled(status);
    updateMutation.mutate(status);
  };

  return (
    <li key={feed.id}>
      <div className="grid grid-cols-12 items-center text-sm font-medium text-gray-900 dark:text-gray-500">
        <div className="col-span-2 sm:col-span-1 pl-6 flex items-center">
          <Checkbox value={feed.enabled} setValue={toggleActive} ariaLabel={`${feed.name} enabled`} />
        </div>
        <div className="col-span-9 sm:col-span-3 pl-10 sm:pl-12 py-3 flex flex-col">
          <span className="pr-2 dark:text-white truncate">{feed.name}</span>
        </div>
        <div className="hidden sm:flex col-span-2 py-3 items-center">
          <span className="truncate text-xs text-gray-500">
            {feedGroup?.name || "Unknown"}
          </span>
        </div>
        <div className="hidden md:flex col-span-2 py-3 items-center">
          <span className="truncate text-xs text-gray-500">{feed.url}</span>
        </div>
        <div className="hidden md:flex col-span-1 py-3 items-center">
          <TierBadge feed={feed} />
        </div>
        <div className="hidden md:flex col-span-2 py-3 items-center sm:px-4">
          <span title={simplifyDate(feed.last_run_at)}>
            {feed.last_run_at ? simplifyDate(feed.last_run_at) : "Never"}
          </span>
        </div>
        <div className="col-span-1 flex justify-center items-center md:px-6">
          <FeedItemDropdown feed={feed} onToggle={toggleActive} />
        </div>
      </div>
    </li>
  );
}

const TIER_STYLES: Record<string, string> = {
  trusted: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 hover:bg-green-200",
  neutral: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-200",
  penalised: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 hover:bg-red-200",
};

function TierBadge({ feed }: { feed: Feed }) {
  const queryClient = useQueryClient();

  const updateTier = useMutation({
    mutationFn: (tier: string) =>
      api.put(`/feeds/${feed.id}`, { source_tier: tier }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feedKeys.all });
      toast.custom((t) => (
        <Toast type="success" body={`${feed.name} tier updated`} t={t} />
      ));
    },
    onError: () => {
      toast.custom((t) => (
        <Toast type="error" body={`Failed to update ${feed.name} tier`} t={t} />
      ));
    },
  });

  return (
    <Menu as="div" className="relative">
      <MenuButton
        className={classNames(
          "px-2 py-1 rounded text-xs font-medium uppercase",
          TIER_STYLES[feed.source_tier] ?? TIER_STYLES.neutral,
        )}
      >
        {feed.source_tier}
      </MenuButton>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <MenuItems className="absolute right-0 z-10 mt-2 w-32 origin-top-right rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          {(["trusted", "neutral", "penalised"] as const).map((tier) => (
            <MenuItem key={tier}>
              {({ focus }) => (
                <button
                  type="button"
                  onClick={() => updateTier.mutate(tier)}
                  className={classNames(
                    focus ? "bg-gray-100 dark:bg-gray-700" : "",
                    "block w-full text-left px-3 py-2 text-xs uppercase",
                    feed.source_tier === tier ? "font-bold" : "",
                  )}
                >
                  {tier}
                </button>
              )}
            </MenuItem>
          ))}
        </MenuItems>
      </Transition>
    </Menu>
  );
}

interface FeedItemDropdownProps {
  feed: Feed;
  onToggle: (newState: boolean) => void;
}

const FeedItemDropdown = ({ feed, onToggle }: FeedItemDropdownProps) => {
  const cancelModalButtonRef = useRef(null);
  const queryClient = useQueryClient();

  const [deleteModalIsOpen, toggleDeleteModal] = useToggle(false);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/feeds/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feedKeys.all });
      toast.custom((t) => (
        <Toast type="success" body={`Feed ${feed?.name} was deleted`} t={t} />
      ));
    },
  });

  const refreshMutation = useMutation({
    mutationFn: (id: string) => api.post(`/feeds/${id}/refresh`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feedKeys.all });
      toast.custom((t) => (
        <Toast type="success" body={`Feed ${feed?.name} refresh started`} t={t} />
      ));
    },
  });

  return (
    <Menu as="div">
      <DeleteModal
        isOpen={deleteModalIsOpen}
        isLoading={deleteMutation.isPending}
        toggle={toggleDeleteModal}
        buttonRef={cancelModalButtonRef}
        deleteAction={() => {
          deleteMutation.mutate(feed.id);
          toggleDeleteModal();
        }}
        title={`Remove feed: ${feed.name}`}
        text="Are you sure you want to remove this feed? This action cannot be undone."
      />
      <MenuButton className="px-4 py-2">
        <EllipsisHorizontalIcon
          className="w-5 h-5 text-gray-700 hover:text-gray-900 dark:text-gray-100 dark:hover:text-gray-400"
          aria-hidden="true"
        />
      </MenuButton>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <MenuItems
          anchor={{ to: "bottom end", padding: "8px" }}
          className="absolute w-56 bg-white dark:bg-gray-825 divide-y divide-gray-200 dark:divide-gray-750 rounded-md shadow-lg border border-gray-250 dark:border-gray-750 focus:outline-hidden z-10"
        >
          <div className="px-1 py-1">
            <MenuItem>
              {({ active }) => (
                <button
                  className={classNames(
                    active ? "bg-blue-600 text-white" : "text-gray-900 dark:text-gray-300",
                    "font-medium group flex rounded-md items-center w-full px-2 py-2 text-sm"
                  )}
                  onClick={() => onToggle(!feed.enabled)}
                >
                  <PencilSquareIcon
                    className={classNames(
                      active ? "text-white" : "text-blue-500",
                      "w-5 h-5 mr-2"
                    )}
                    aria-hidden="true"
                  />
                  Toggle
                </button>
              )}
            </MenuItem>
            <MenuItem>
              {({ active }) => (
                <button
                  className={classNames(
                    active ? "bg-blue-600 text-white" : "text-gray-900 dark:text-gray-300",
                    "font-medium group flex rounded-md items-center w-full px-2 py-2 text-sm"
                  )}
                  onClick={() => refreshMutation.mutate(feed.id)}
                >
                  <ArrowPathIcon
                    className={classNames(
                      active ? "text-white" : "text-blue-500",
                      "w-5 h-5 mr-2"
                    )}
                    aria-hidden="true"
                  />
                  Refresh
                </button>
              )}
            </MenuItem>
            <MenuItem>
              {({ active }) => (
                <button
                  className={classNames(
                    active ? "bg-blue-600 text-white" : "text-gray-900 dark:text-gray-300",
                    "font-medium group flex rounded-md items-center w-full px-2 py-2 text-sm"
                  )}
                  onClick={() => {
                    const apiUrl = `${window.location.origin}/api/v1/feeds/${feed.id}`;
                    navigator.clipboard.writeText(apiUrl);
                    toast.custom((t) => (
                      <Toast type="success" body="API URL copied to clipboard" t={t} />
                    ));
                  }}
                >
                  <ClipboardDocumentIcon
                    className={classNames(
                      active ? "text-white" : "text-blue-500",
                      "w-5 h-5 mr-2"
                    )}
                    aria-hidden="true"
                  />
                  Copy API URL
                </button>
              )}
            </MenuItem>
          </div>
          <div className="px-1 py-1">
            <MenuItem>
              {({ active }) => (
                <button
                  className={classNames(
                    active ? "bg-red-600 text-white" : "text-gray-900 dark:text-gray-300",
                    "font-medium group flex rounded-md items-center w-full px-2 py-2 text-sm"
                  )}
                  onClick={() => toggleDeleteModal()}
                >
                  <TrashIcon
                    className={classNames(
                      active ? "text-white" : "text-red-500",
                      "w-5 h-5 mr-2"
                    )}
                    aria-hidden="true"
                  />
                  Delete
                </button>
              )}
            </MenuItem>
          </div>
        </MenuItems>
      </Transition>
    </Menu>
  );
};

export default FeedSettings;
